/**
 * composite.v1 cizici — ortak uyaran + alt sorular (legacy A6, E1).
 *
 * Kritik davranis: alt sorular TEK TEK gosterilir; cocuk isaretleyince "ileri"
 * ile bir sonrakine gecer (urun istegi, ozellikle s15 = 6 satir ayri ayri).
 * Legacy A6 hepsini ust uste basiyordu; Runner sirali gosterir.
 *
 * Her alt soru bir choice.v1'dir, o yuzden ChoiceItem yeniden kullanilir.
 *
 * Cevap sekli: { parts: { <partId>: { selectedOptionIds: [...] } } } — sunucu
 * tarafindaki CompositeV1Handler.Score bunu birebir okur (her parcayi choice.v1
 * kurallariyla puanlayip ortalar). Cevaplanmayan parca sunucuda 0 sayilir.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { MechanicAnswer, MechanicProps, SessionItem } from "./types";
import { ChoiceItem } from "./ChoiceItem";
import type { CompositeContent } from "./content";

export function CompositeItem({ item, onAnswerChange }: MechanicProps) {
  const content = item.content as unknown as CompositeContent;
  const shared = content.sharedPrompt ?? {};
  const parts = content.parts ?? [];

  const [ix, setIx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, MechanicAnswer | null>>({});

  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    // Butun alt sorular cevaplanana kadar null yay: player'in "ileri"si ancak
    // composite tamamlaninca acilsin (her parca kendi icinde ilerliyor).
    const allDone =
      parts.length > 0 &&
      parts.every((p) => {
        const ids = answers[p.id]?.selectedOptionIds as string[] | undefined;
        return !!ids && ids.length > 0;
      });
    const anyDone = Object.values(answers).some((a) => {
      const ids = a?.selectedOptionIds as string[] | undefined;
      return !!ids && ids.length > 0;
    });
    cb.current(allDone ? { parts: answers } : null, anyDone ? { parts: answers } : null);
  }, [answers, parts]);

  const part = parts[ix];
  const canAdvance = useMemo(() => {
    const a = answers[part?.id ?? ""];
    const ids = a?.selectedOptionIds as string[] | undefined;
    return !!ids && ids.length > 0;
  }, [answers, part]);

  if (!part) return <div className="mech">Alt soru yok.</div>;

  // s15'te parca yonergesi "N. satir" -> gizle (uyaran zaten satirlari gosteriyor).
  const rawContent = part.content;
  const partContent =
    rawContent.prompt && /^\d+\.\s*satır$/i.test(rawContent.prompt.text ?? "")
      ? { ...rawContent, prompt: { ...rawContent.prompt, text: "" } }
      : rawContent;
  const subItem: SessionItem = {
    ...item,
    mechanic: "choice.v1",
    content: partContent as unknown as SessionItem["content"],
    answer: answers[part.id] ?? undefined,
  };

  // Uzun metin = hikaye (uygulayici okur); kisa metin = yonerge basligi.
  const sharedText = shared.text ?? shared.story ?? "";
  const isStory = sharedText.length > 120;

  return (
    <div className="mech composite">
      {sharedText && !isStory && <h2 className="prompt">{sharedText}</h2>}
      {sharedText && isStory && <p className="story-text">{sharedText}</p>}
      {shared.imageUrl && (
        <div className="stimulus shared-stimulus">
          <img src={shared.imageUrl} alt="" />
        </div>
      )}

      <div className="part-card">
        <ChoiceItem
          key={ix}
          item={subItem}
          onAnswerChange={(a) => setAnswers((s) => ({ ...s, [part.id]: a }))}
        />
      </div>

      <div className="part-nav">
        <button
          type="button"
          className="ghost-btn"
          disabled={ix === 0}
          onClick={() => setIx((i) => Math.max(0, i - 1))}
        >
          ‹ geri
        </button>
        {ix < parts.length - 1 ? (
          <button
            type="button"
            className="primary"
            disabled={!canAdvance}
            onClick={() => setIx((i) => i + 1)}
          >
            ileri ›
          </button>
        ) : (
          <span className={canAdvance ? "done-tag" : "hint-tag"}>
            {canAdvance ? "✓ tamamlandı" : "işaretle"}
          </span>
        )}
      </div>
    </div>
  );
}
